import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

export const Page = ({ children, narrow = false }) => (
  <div className={`mx-auto w-full px-4 sm:px-6 py-5 sm:py-8 ${narrow ? "max-w-xl" : "max-w-4xl"}`}>
    {children}
  </div>
);

export const Progress = ({ current, total }) => (
  <div className="h-[3px] w-full bg-[#E5E7EB] rounded-full overflow-hidden" data-testid="wizard-progress">
    <div
      className="h-full bg-[#142D43] transition-[width] duration-300"
      style={{ width: `${Math.min(100, (current / total) * 100)}%` }}
    />
  </div>
);

const primaryBtn =
  "bg-[#142D43] text-white border border-[rgba(255,255,255,0.08)] shadow-[0_2px_0_#0F2233,0_6px_14px_rgba(20,45,67,0.16),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#1E3F5C] hover:shadow-[0_4px_0_#0F2233,0_10px_22px_rgba(20,45,67,0.20),inset_0_1px_0_rgba(255,255,255,0.15)] hover:-translate-y-[2px] active:shadow-[0_1px_0_#0F2233,0_3px_7px_rgba(20,45,67,0.14),inset_0_1px_2px_rgba(0,0,0,0.08)] active:translate-y-[2px] font-semibold tracking-[0.01em] px-7 rounded-[14px] transition-[background-color,transform,box-shadow] duration-[180ms] active:duration-100 text-[15px] h-[54px] sm:h-[56px] flex items-center justify-center cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none w-full sm:w-auto sm:min-w-[200px]";
const secondaryBtn =
  "bg-white text-[#142D43] border border-[#E5E7EB] shadow-[0_3px_10px_rgba(20,45,67,0.06)] hover:bg-[#F4F6F8] hover:border-[rgba(20,45,67,0.25)] hover:-translate-y-[2px] active:translate-y-[1px] active:shadow-[0_1px_4px_rgba(20,45,67,0.06)] font-semibold tracking-[0.01em] px-7 rounded-[14px] transition-[background-color,border-color,transform,box-shadow] duration-[180ms] active:duration-100 text-[15px] h-[54px] sm:h-[56px] flex items-center justify-center cursor-pointer w-full sm:w-auto sm:min-w-[160px]";

export const NavButtons = ({ onBack, onNext, canNext, nextLabel = "CONTINUA", showBack = true, loading = false, nextTestId = "wizard-next-button", hideNext = false }) => (
  <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between">
    {showBack ? (
      <button type="button" data-testid="wizard-back-button" onClick={onBack} className={secondaryBtn}>
        INDIETRO
      </button>
    ) : (
      <span className="hidden sm:block" />
    )}
    {!hideNext ? (
      <button type="button" data-testid={nextTestId} disabled={!canNext || loading} onClick={onNext} className={primaryBtn}>
        {loading ? "INVIO IN CORSO…" : nextLabel}
      </button>
    ) : null}
  </div>
);

export const slug = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 40);

export const DisclaimerBlock = ({ content, checked, onToggle, testId = "disclaimer-checkbox", withCheckbox = true }) => (
  <div className="mt-6" data-testid="disclaimer-block">
    {content.paragraphs.map((p, i) => (
      <p key={i} className="mb-3 text-sm sm:text-base text-[#1E3F5C] leading-relaxed">
        {p}
      </p>
    ))}
    {withCheckbox ? (
      <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="disclaimer-label">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          data-testid={testId}
          className="mt-1 h-5 w-5 shrink-0 accent-[#142D43]"
        />
        <span className="text-sm text-[#142D43] leading-relaxed">{content.checkbox}</span>
      </label>
    ) : null}
  </div>
);

export const CyclingText = ({ name, desc }) => {
  const [showDesc, setShowDesc] = useState(false);
  const [reduce] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    if (reduce) return undefined;
    const id = setInterval(() => setShowDesc((v) => !v), 3000);
    return () => clearInterval(id);
  }, [reduce]);
  if (reduce) return <span className="whitespace-nowrap">{name}</span>;
  return (
    <span className="grid overflow-hidden whitespace-nowrap" data-testid={`cycling-text-${slug(name)}`}>
      <span
        style={{ gridArea: "1 / 1" }}
        className={`whitespace-nowrap transition-[opacity,transform] duration-[400ms] ease-in-out ${
          showDesc ? "opacity-0 -translate-x-2" : "opacity-100 translate-x-0"
        }`}
      >
        {name}
      </span>
      <span
        aria-hidden="true"
        style={{ gridArea: "1 / 1" }}
        className={`whitespace-nowrap self-center text-[12.5px] leading-tight transition-[opacity,transform] duration-[400ms] ease-in-out ${
          showDesc ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"
        }`}
      >
        {desc}
      </span>
    </span>
  );
};

export const OptionList = ({ options, values, onChange, exclusive = null, icons = null, descriptions = null }) => {
  const toggle = (opt) => {
    let next;
    if (values.includes(opt)) {
      next = values.filter((v) => v !== opt);
    } else if (exclusive && opt === exclusive) {
      next = [exclusive];
    } else {
      next = exclusive ? [...values.filter((v) => v !== exclusive), opt] : [...values, opt];
    }
    onChange(next);
  };
  return (
    <div className="mt-6">
      <div className="flex flex-wrap gap-2">
        {options
          .filter((opt) => opt !== exclusive)
          .map((opt) => {
            const selected = values.includes(opt);
            const Icon = icons ? icons[opt] : null;
            const desc = descriptions ? descriptions[opt] : null;
            return (
              <button
                key={opt}
                type="button"
                data-testid={`option-${slug(opt)}`}
                onClick={() => toggle(opt)}
                className={`min-h-[44px] px-4 py-2 rounded-[12px] text-sm inline-flex items-center gap-1.5 transition-[border-color,background-color,box-shadow,transform] duration-[180ms] ${
                  selected
                    ? "bg-[#F4F6F8] border-2 border-[#142D43] text-[#142D43] font-semibold shadow-[0_3px_10px_rgba(20,45,67,0.08)] active:translate-y-[1px]"
                    : "bg-white border border-[#E5E7EB] text-[#142D43] hover:border-[rgba(20,45,67,0.25)] hover:bg-[#F4F6F8] shadow-[0_3px_10px_rgba(20,45,67,0.05)] active:translate-y-[1px]"
                }`}
              >
                {Icon ? <Icon size={15} strokeWidth={1.8} className="shrink-0" data-testid={`option-icon-${slug(opt)}`} /> : null}
                {desc ? <CyclingText name={opt} desc={desc} /> : opt}
              </button>
            );
          })}
      </div>
      {exclusive ? (
        <button
          type="button"
          data-testid={`option-${slug(exclusive)}`}
          onClick={() => toggle(exclusive)}
          className={`mt-3 w-full min-h-[52px] px-5 rounded-[14px] text-[15px] transition-[border-color,background-color,box-shadow,transform] duration-[180ms] ${
            values.includes(exclusive)
              ? "bg-[#F4F6F8] border-2 border-[#142D43] text-[#142D43] font-semibold shadow-[0_3px_10px_rgba(20,45,67,0.08)] active:translate-y-[1px]"
              : "bg-white border border-[#E5E7EB] text-[#142D43] hover:border-[rgba(20,45,67,0.25)] hover:bg-[#F4F6F8] shadow-[0_3px_10px_rgba(20,45,67,0.05)] active:translate-y-[1px]"
          }`}
        >
          {exclusive}
        </button>
      ) : null}
    </div>
  );
};

export const GradientSlider = ({ min, max, step = 1, value, displayValue, onChange, gradient, leftLabel, centerLabel, rightLabel, testId }) => {
  const clampStep = (dir) => {
    const next = Math.round((value + dir * step) * 10) / 10;
    onChange(Math.min(max, Math.max(min, next)));
  };
  const adjBtn =
    "w-11 h-11 rounded-[12px] text-xl font-semibold text-[#142D43] bg-white border border-[#E5E7EB] shadow-[0_2px_0_#D1D5DB,0_4px_10px_rgba(20,45,67,0.08)] hover:border-[#142D43] active:translate-y-[1px] transition-[border-color,transform,box-shadow] duration-[180ms] cursor-pointer flex items-center justify-center select-none";
  return (
    <div className="mt-6">
      <div className="text-center text-4xl font-semibold text-[#142D43] tabular-nums" data-testid={`${testId}-value`}>
        {displayValue}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="pip-slider mt-8"
        style={{ background: gradient }}
        data-testid={testId}
      />
      <div className="mt-4 flex justify-between gap-2 text-xs text-[#374151]">
        <div className="max-w-[30%] flex flex-col items-start gap-2">
          <span>{leftLabel}</span>
          <button type="button" aria-label="Diminuisci" data-testid={`${testId}-minus`} onClick={() => clampStep(-1)} className={adjBtn}>
            −
          </button>
        </div>
        {centerLabel ? <span className="text-center">{centerLabel}</span> : null}
        <div className="max-w-[30%] flex flex-col items-end gap-2 text-right">
          <span>{rightLabel}</span>
          <button type="button" aria-label="Aumenta" data-testid={`${testId}-plus`} onClick={() => clampStep(1)} className={adjBtn}>
            +
          </button>
        </div>
      </div>
    </div>
  );
};

export const BigTextInput = ({ value, onChange, placeholder, testId, type = "text", autoFocus = false }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    autoFocus={autoFocus}
    data-testid={testId}
    className="mt-6 w-full min-h-[52px] px-4 py-3 rounded-xl border border-[#E5E7EB] bg-white text-base text-[#142D43] focus:border-[#142D43] focus:shadow-[0_0_0_3px_rgba(20,45,67,0.08)] outline-none transition-[border-color,box-shadow,transform]"
  />
);

export const BigTextArea = ({ value, onChange, placeholder, testId }) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={5}
    data-testid={testId}
    className="mt-6 w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-white text-base text-[#142D43] focus:border-[#142D43] focus:shadow-[0_0_0_3px_rgba(20,45,67,0.08)] outline-none transition-[border-color,box-shadow,transform] resize-y"
  />
);

export const YesNo = ({ value, onChange }) => (
  <div className="mt-6 flex flex-col gap-3">
    {[
      { v: "si", label: "SÌ" },
      { v: "no", label: "NO" },
    ].map(({ v, label }) => (
      <button
        key={v}
        type="button"
        data-testid={`yesno-${v}`}
        onClick={() => onChange(v)}
        className={`w-full min-h-[56px] px-5 rounded-[14px] text-lg transition-[border-color,background-color,box-shadow,transform] duration-[180ms] ${
          value === v
            ? "bg-[#F4F6F8] border-2 border-[#142D43] text-[#142D43] font-semibold shadow-[0_3px_10px_rgba(20,45,67,0.08)] active:translate-y-[1px]"
            : "bg-white border border-[#E5E7EB] text-[#142D43] hover:border-[rgba(20,45,67,0.25)] hover:bg-[#F4F6F8] shadow-[0_3px_10px_rgba(20,45,67,0.05)] active:translate-y-[1px]"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

export const ConfirmScreen = ({ title, text, note }) => (
  <Page narrow>
    <div className="text-center py-10" data-testid="confirmation-screen">
      <CheckCircle2 className="mx-auto text-[#142D43]" size={48} strokeWidth={1.5} />
      <h1 className="font-display mt-6 text-3xl sm:text-4xl font-semibold text-[#142D43] tracking-tight">{title}</h1>
      <p className="mt-4 text-base text-[#142D43] leading-relaxed">{text}</p>
      {note ? <p className="mt-6 text-sm text-[#374151] leading-relaxed">{note}</p> : null}
      <Link
        to="/"
        data-testid="back-home-button"
        className="mt-10 inline-flex items-center justify-center bg-[#142D43] text-white border border-[rgba(255,255,255,0.08)] shadow-[0_2px_0_#0F2233,0_6px_14px_rgba(20,45,67,0.16),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#1E3F5C] hover:shadow-[0_4px_0_#0F2233,0_10px_22px_rgba(20,45,67,0.20),inset_0_1px_0_rgba(255,255,255,0.15)] hover:-translate-y-[2px] active:shadow-[0_1px_0_#0F2233,0_3px_7px_rgba(20,45,67,0.14),inset_0_1px_2px_rgba(0,0,0,0.08)] active:translate-y-[2px] font-semibold tracking-[0.01em] px-8 rounded-[14px] transition-[background-color,transform,box-shadow] duration-[180ms] active:duration-100 text-[15px] h-[54px]"
      >
        TORNA ALLA HOME
      </Link>
    </div>
  </Page>
);

export const StepTitle = ({ title, sub, desc }) => (
  <div className="mt-5">
    <h1 className="font-display text-2xl sm:text-3xl font-semibold text-[#142D43] leading-snug">{title}</h1>
    {desc ? <p className="mt-3 text-sm text-[#374151] leading-relaxed">{desc}</p> : null}
    {sub ? <p className="mt-2 text-sm sm:text-base text-[#374151] leading-relaxed">{sub}</p> : null}
  </div>
);
